import { useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useOnRampRouter } from '@onramp/hooks/useOnRampRouter';
import { isBaseKilledAtom } from '@onramp/state/killSwitches/killSwitchesState';
import exitWidget from '@onramp/utils/exitWidget';
import { useIsGuestCheckoutPath } from '@onramp/utils/guestCheckoutUtils';
import { useKillSwitches } from '@onramp/utils/killswitches/useKillSwitches';
import { isEmbeddedExperience, isMobileExperience } from '@onramp/utils/postMessage';
import { useSetAtom } from 'jotai';
import { Button } from '@cbhq/cds-web/buttons';
import { Pictogram } from '@cbhq/cds-web/illustrations';
import { VStack } from '@cbhq/cds-web/layout';
import { Link, TextBody, TextTitle3 } from '@cbhq/cds-web/typography';

import { PageWrapper } from './PageWrapper';

const STATUS_PAGE_URL = 'https://status.coinbase.com/';

const statusPageLink = function link(text: NonNullable<ReactNode>) {
  return (
    <Link href={STATUS_PAGE_URL} target="_blank">
      {text}
    </Link>
  );
};

export default function KillSwitchGuard({ children }: { children: ReactNode }) {
  const switches = useKillSwitches();
  const isGuestCheckout = useIsGuestCheckoutPath();
  const isBaseKilled = isGuestCheckout
    ? switches.kill_cbpay_guest_base_support
    : switches.kill_cbpay_base_support;
  const setIsBaseKilled = useSetAtom(isBaseKilledAtom);

  useEffect(() => {
    setIsBaseKilled(isBaseKilled);
  }, [isBaseKilled, setIsBaseKilled]);

  return switches.kill_cb_pay || switches.kill_onramp_planned_ledger_downtime ? (
    <CBPayIsKilled />
  ) : (
    <>{children}</>
  );
}

const messages = defineMessages({
  headerText: {
    id: 'KillSwitchGuard.headerText',
    defaultMessage: 'Coinbase Onramp',
    description: 'Header text',
  },
  disabledMessageTitle: {
    id: 'KillSwitchGuard.disabledMessageTitle',
    defaultMessage: 'Coinbase Onramp is temporarily unavailable',
    description: 'Title to message indicating Coinbase Onramp is down.',
  },
  disabledMessage: {
    id: 'KillSwitchGuard.disabledMessage',
    defaultMessage:
      "We're working to resolve this issue. Check the <statusPageLink>status page</statusPageLink> for more information.",
    description: 'Message indicating Coinbase Onramp is temporarily unavailable.',
  },
  checkStatus: {
    id: 'KillSwitchGuard.checkStatus',
    defaultMessage: 'See status',
    description: 'button text',
  },
});

function CBPayIsKilled() {
  const { formatMessage } = useIntl();
  const { goBack } = useOnRampRouter();
  const handleOnBack = useCallback(() => {
    const isMobileOrEmbedded = isEmbeddedExperience() || isMobileExperience();

    if (isMobileOrEmbedded) {
      exitWidget();
    } else {
      goBack();
    }
  }, [goBack]);

  return (
    <PageWrapper headerText={formatMessage(messages.headerText)} onBack={handleOnBack}>
      <VStack alignItems="center" spacing={3} spacingTop={8}>
        <Pictogram name="settings" dimension="64x64" />
        <TextTitle3 as="div" align="center" spacingBottom={2} spacingTop={3}>
          {formatMessage(messages.disabledMessageTitle)}
        </TextTitle3>
        <TextBody as="div" align="center" spacingBottom={2} color="foregroundMuted">
          {formatMessage(messages.disabledMessage, {
            statusPageLink,
          })}
        </TextBody>
      </VStack>
      <VStack spacing={3} flexGrow={1} justifyContent="flex-end" width="100%">
        <Button href={STATUS_PAGE_URL} variant="secondary" target="_blank">
          {formatMessage(messages.checkStatus)}
        </Button>
      </VStack>
    </PageWrapper>
  );
}
